

























































































































import Vue from 'vue';
import VueCompositionAPI, {defineComponent, ref, computed, Ref} from '@vue/composition-api';
import {ToggleComponent} from '@mobi-components/toggle';
import {VPopover, VClosePopover} from 'v-tooltip';
import {truncate} from './utils';
import HeartIcon from '@components/svg/heart.vue';
import FilledHeartIcon from '@components/svg/filled-heart.vue';

Vue.use(VueCompositionAPI);

// @ts-ignore
import ArrowDown from './arrow-down.vue';
// @ts-ignore
import loading from './loader.vue';
import PlaceCard from '@common/card/place-card.vue';

import RadioInput from './radio-input.vue';
import SchedulePopover from './schedule-popover.vue';
import {POIDetailsProps, RecommendationCategory, Timeslot, PoiItem, LunchTime} from './types';
import {useMealToggle} from './meal-toggle';

const EXPERIENCE = 'EXPERIENCE';

export function useTruncText(description: Ref<string>, maxTextLength = 300) {
  const isTextTruncated = ref(true);

  const visibleDescription = computed(() => {
    if (isTextTruncated.value) {
      return truncate(description.value, maxTextLength, true);
    }
    return description.value;
  });
  const truncCaption = computed(() => {
    return isTextTruncated.value ? 'Read More' : 'Collapse';
  });
  const isTruncBtnVisible = computed(() => {
    return description.value.length > maxTextLength;
  });

  const toggleTruncate = () => {
    isTextTruncated.value = !isTextTruncated.value;
  };

  return {
    isTruncBtnVisible,
    truncCaption,
    visibleDescription,
    toggleTruncate,
  };
}

const AmexAdditionalActivitiesList = defineComponent({
  components: {
    ArrowDown,
    HeartIcon,
    FilledHeartIcon,
    loading,
    // Pill,
    PlaceCard,
    ToggleComponent,
    RadioInput,
    SchedulePopover,
    VPopover,
  },
  directives: {
    'close-popover': VClosePopover,
  },
  props: {
    selectedPoi: Object,
    // isMapViewAvailable: {
    //   type: Boolean,
    //   default: false,
    // },
    alternatives: {
      type: Array,
      default: () => [],
    },
    rooms: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    contextName: {
      type: String,
      default: '',
    },
    savedPoiIds: {
      type: Set,
      default: () => new Set(),
    },
  },
  setup(props: POIDetailsProps, {emit}) {
    const pendingActivity = ref<PoiItem | null>(null);
    const isTimeSlotsPending = ref<boolean>(true);
    const availableTimeSlots = ref<Timeslot[]>([]);

    const rating = computed(() => {
      return props.selectedPoi?.rating || 0;
    });

    const matchedTags = computed(() => {
      return props.selectedPoi?.tags?.filter((x) => x.isActive).map((x) => x.name) || [];
    });

    const otherTags = computed(() => {
      return props.selectedPoi?.tags?.filter((x) => !x.isActive).map((x) => x.name) || [];
    });

    const description = computed(() => {
      return props.selectedPoi?.description || '';
    });
    const address = computed(() => {
      return props.selectedPoi?.location?.address || '';
    });
    const web = computed(() => {
      return props.selectedPoi?.booking_url || props.selectedPoi?.url || '';
    });

    const forYou = computed(() => {
      const cat = props.selectedPoi?.recommendations?.find((x) => x.category === RecommendationCategory.FOR_YOU);
      return cat?.content || '';
    });
    const forTrip = computed(() => {
      const cat = props.selectedPoi?.recommendations?.find((x) => x.category === RecommendationCategory.FOR_TRIP);
      return cat?.content || '';
    });
    const forContext = computed(() => {
      const cat = props.selectedPoi?.recommendations?.find((x) => x.category === RecommendationCategory.FOR_CONTEXT);
      return cat?.content || '';
    });

    const images = computed(() => {
      const indexedImg = new Array(5)
        .fill(null)
        .map((_, index) => ((props.selectedPoi?.images || []) as Record<number, string>)[index] || '');

      const allImgs = [...indexedImg].filter(Boolean).filter((x, index, self) => self.indexOf(x) === index);
      return [...allImgs];
    });
    const activeImg = ref(0);
    const slideLeft = () => {
      activeImg.value = Math.max(activeImg.value - 1, 0);
    };
    const slideRight = () => {
      activeImg.value = Math.min(activeImg.value + 1, images.value.length);
    };

    const onShowDetails = async (alternativeHotelId: string) => {
      emit('show-details', alternativeHotelId);
    };

    const category = computed(() => {
      const categories = props.selectedPoi?.categories || [];
      return categories.length ? categories[0] : EXPERIENCE;
    });

    const onQuickAddBtnClick = (poi: PoiItem) => {
      emit('add', {lunchTime: LunchTime.NONE, poiId: poi.id});
    };

    const {/*getSelectedLunchTime, onMealChange, */ ...mealProps} = useMealToggle(category);

    const onThumbClick = (index: number) => {
      activeImg.value = index;
    };

    const reviews = computed(() => {
      return (props.selectedPoi?.sourced_data?.reviews || []).map((x, index) => ({
        id: index,
        text: x.content,
        rating: x.rating,
      }));
    });

    const numberOfReviews = computed(() => {
      const obj = props.selectedPoi?.sourced_data?.sourced_num_reviews || {};
      return Object.keys(obj).reduce((res, key) => obj[key] + res, 0);
    });

    const sourceOfReviews = computed(() => {
      const obj = props.selectedPoi?.sourced_data?.sourced_num_reviews || {};
      return Object.keys(obj).find(Boolean);
    });

    const isReviewTextTruncated = ref(true);
    const visibleReviews = computed(() => {
      if (isReviewTextTruncated.value) {
        return reviews.value.slice(0, 1);
      }
    });
    const showMoreButtonText = computed(() => {
      return isReviewTextTruncated.value ? 'Read More' : 'Collapse';
    });

    const toggleShowMoreReviews = () => {
      isReviewTextTruncated.value = !isReviewTextTruncated.value;
    };

    const isSaved = (id: string) => {
      return props.savedPoiIds.has(id);
    };

    return {
      rating,
      matchedTags,
      otherTags,
      images,
      slideLeft,
      slideRight,
      activeImg,
      category,
      onShowDetails,
      pendingActivity,
      onQuickAddBtnClick,
      ...useTruncText(description),
      address,
      web,
      forYou,
      forTrip,
      forContext,
      ...mealProps,
      isTimeSlotsPending,
      availableTimeSlots,
      onThumbClick,
      reviews,
      numberOfReviews,
      sourceOfReviews,
      toggleShowMoreReviews,
      showMoreButtonText,
      visibleReviews,
      isReviewTextTruncated,
      isSaved,
    };
  },
});

export default AmexAdditionalActivitiesList;
